import { cn } from "@faire/prototype-playground/lib/utils";
import {
  Plus,
  Trash,
  DotsSixVertical,
  Sparkle,
  FolderOpen,
  UploadSimple,
  X,
  Check,
  Crosshair,
} from "@phosphor-icons/react";
import React, { useState, useRef, useCallback, useEffect } from "react";

import type {
  TableConfig,
  SavedTableConfig,
  Column,
  CellType,
  RowAction,
} from "../../types/table";
import {
  CELL_TYPE_OPTIONS,
  WIDTH_OPTIONS,
  CELL_TYPE_KEY_MAP,
  DEFAULT_TABLE_CONFIG,
  MOCK_ROWS,
  genColumnId,
  genActionId,
} from "../../types/table";

import { Button as UiButton } from "./slate-table-ui/button";
import { Checkbox as UiCheckbox } from "./slate-table-ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./slate-table-ui/dialog";
import { Input } from "./slate-table-ui/input";
import { Label } from "./slate-table-ui/label";
import {
  Select as UiSelect,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from "./slate-table-ui/select";

// ─── Props ──────────────────────────────────────────────────────────────────

interface SlateTableConfigPanelProps {
  config: TableConfig;
  configName: string;
  onConfigChange: (config: TableConfig) => void;
  onConfigNameChange: (name: string) => void;
  onSave: () => void;
  onClose: () => void;
  // For load modal
  savedConfigs: SavedTableConfig[];
  onLoad: (config: SavedTableConfig) => void;
  onDelete: (id: string) => void;
  /** Scroll the active table into view */
  onScrollToTable?: () => void;
  /** Background opacity 0-100 (default 40) */
  bgOpacity?: number;
  /** Backdrop blur in px (default 12) */
  bgBlur?: number;
}

// ─── Row Count Input (local string state for free typing) ────────────────────

function RowCountInput({
  value,
  onChange,
}: {
  value: number;
  onChange: (v: number) => void;
}) {
  const [draft, setDraft] = useState(String(value));

  // Sync draft when value changes externally (e.g. +/- buttons)
  useEffect(() => {
    setDraft(String(value));
  }, [value]);

  return (
    <Input
      type="text"
      inputMode="numeric"
      value={draft}
      onChange={(e) => {
        const raw = e.target.value.replace(/[^0-9]/g, "");
        setDraft(raw);
        const num = parseInt(raw, 10);
        if (!isNaN(num) && num >= 1) {
          onChange(num);
        }
      }}
      onBlur={() => {
        const num = parseInt(draft, 10);
        if (isNaN(num) || num < 1) {
          onChange(1);
          setDraft("1");
        }
      }}
      className="h-7 w-14 [appearance:textfield] text-center text-xs [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
    />
  );
}

// ─── Component ──────────────────────────────────────────────────────────────

export function SlateTableConfigPanel({
  config,
  configName,
  onConfigChange,
  onConfigNameChange,
  onSave,
  onClose,
  savedConfigs,
  onLoad,
  onDelete,
  onScrollToTable,
  bgOpacity = 40,
  bgBlur = 12,
}: SlateTableConfigPanelProps) {
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const dragColIndex = useRef<number | null>(null);

  const [generatingColumn, setGeneratingColumn] = useState<string | null>(null);
  const [showLoadModal, setShowLoadModal] = useState(false);
  const uploadInputRef = useRef<HTMLInputElement>(null);

  const [saveToast, setSaveToast] = useState(false);
  const saveToastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Column operations ─────────────────────────────────────────────────────

  function updateColumn(colId: string, patch: Partial<Column>) {
    onConfigChange({
      ...config,
      columns: config.columns.map((c) =>
        c.id !== colId ? c : { ...c, ...patch }
      ),
    });
  }

  function updateCellType(colId: string, newType: CellType) {
    onConfigChange({
      ...config,
      columns: config.columns.map((c) =>
        c.id !== colId
          ? c
          : {
              ...c,
              cellType: newType,
              key: CELL_TYPE_KEY_MAP[newType],
              bold:
                newType === "text" || newType === "imageAndText"
                  ? c.bold
                  : false,
            }
      ),
    });
  }

  function addColumn() {
    const id = genColumnId();
    onConfigChange({
      ...config,
      columns: [
        ...config.columns,
        {
          id,
          label: "New Column",
          cellType: "text",
          width: 180,
          key: "text",
          bold: false,
        },
      ],
    });
  }

  function deleteColumn(colId: string) {
    onConfigChange({
      ...config,
      columns: config.columns.filter((c) => c.id !== colId),
    });
  }

  // ── Row action operations ─────────────────────────────────────────────────

  function updateRowAction(actionId: string, patch: Partial<RowAction>) {
    onConfigChange({
      ...config,
      rowActionItems: config.rowActionItems.map((a) =>
        a.id !== actionId ? a : { ...a, ...patch }
      ),
    });
  }

  function addRowAction() {
    onConfigChange({
      ...config,
      rowActionItems: [
        ...config.rowActionItems,
        {
          id: genActionId(),
          label: "Action",
          display: "overflow",
          variant: "secondary",
        },
      ],
    });
  }

  function deleteRowAction(actionId: string) {
    onConfigChange({
      ...config,
      rowActionItems: config.rowActionItems.filter((a) => a.id !== actionId),
    });
  }

  // ── Drag-and-drop ─────────────────────────────────────────────────────────

  function handleDragStart(index: number) {
    dragColIndex.current = index;
  }

  function handleDragOver(e: React.DragEvent, index: number) {
    e.preventDefault();
    setDragOverIndex(index);
  }

  function handleDrop(index: number) {
    if (dragColIndex.current === null || dragColIndex.current === index) return;
    const next = [...config.columns];
    const [moved] = next.splice(dragColIndex.current, 1);
    next.splice(index, 0, moved);
    onConfigChange({ ...config, columns: next });
    dragColIndex.current = null;
    setDragOverIndex(null);
  }

  function handleDragEnd() {
    dragColIndex.current = null;
    setDragOverIndex(null);
  }

  // ── AI fill ───────────────────────────────────────────────────────────────

  // Compute all row IDs (mock + generated) for the current rowCount
  function getAllRowIds(): string[] {
    const ids = MOCK_ROWS.slice(
      0,
      Math.min(config.rowCount, MOCK_ROWS.length)
    ).map((r) => r.id);
    for (let i = MOCK_ROWS.length; i < config.rowCount; i++) {
      ids.push(`rg-${i + 1}`);
    }
    return ids;
  }

  async function fillColumnWithAI(col: Column) {
    const prompt = (config.columnPrompts[col.id] ?? "").trim();
    if (!prompt) return;
    setGeneratingColumn(col.id);
    try {
      const rowIds = getAllRowIds();
      const rowCount = rowIds.length;
      const userMessage = `Generate exactly ${rowCount} short, realistic values for a "${col.cellType}" table column.
Column description: "${prompt}"

Rules:
- Return ONLY a valid JSON array of ${rowCount} strings, no other text
- For "tagGroup": each string is comma-separated tags e.g. "Design, Marketing"
- For "icon": each string must be exactly one of: check, x, alertCircle
- For "imageAndText": provide ${rowCount} product or item names only (no URLs)
- For "link": each string is short link text e.g. "View details", "Learn more"
- Keep values concise and realistic`;

      const res = await fetch("/api/anthropic/v1/messages", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "claude-haiku-4-5-20251001",
          max_tokens: Math.max(600, rowCount * 50),
          messages: [{ role: "user", content: userMessage }],
        }),
      });
      if (!res.ok)
        throw new Error(`API error ${res.status}: ${await res.text()}`);
      const data = await res.json();
      const raw = data.content[0].text
        .trim()
        .replace(/^```[a-z]*\n?/i, "")
        .replace(/```$/, "");
      const values: string[] = JSON.parse(raw);
      const nextCellValues = { ...config.cellValues };
      rowIds.forEach((rowId, i) => {
        nextCellValues[`${col.id}--${rowId}`] = values[i] ?? "";
      });
      onConfigChange({ ...config, cellValues: nextCellValues });
    } catch (err) {
      console.error("AI fill failed:", err);
    } finally {
      setGeneratingColumn(null);
    }
  }

  // ── New table ─────────────────────────────────────────────────────────────

  function handleNewTable() {
    onConfigChange({ ...DEFAULT_TABLE_CONFIG });
    onConfigNameChange("Untitled table");
  }

  // ── AI row generation ───────────────────────────────────────────────────

  const [generatingRows, setGeneratingRows] = useState(false);

  async function generateRowsWithAI(
    newRowCount: number,
    prevRowCount: number,
    currentConfig: TableConfig
  ) {
    if (newRowCount <= MOCK_ROWS.length) return;
    const startIdx = Math.max(prevRowCount, MOCK_ROWS.length);
    const newRowIds = Array.from(
      { length: newRowCount - startIdx },
      (_, i) => `rg-${startIdx + i + 1}`
    );
    if (newRowIds.length === 0) return;

    // Build context from existing cell values, falling back to MOCK_ROWS defaults
    const colContext = currentConfig.columns.map((col) => {
      // First try cellValues overrides
      let existing = Object.entries(currentConfig.cellValues)
        .filter(([k]) => k.startsWith(`${col.id}--`))
        .map(([, v]) => v)
        .filter(Boolean)
        .slice(0, 6);
      // Fall back to MOCK_ROWS default values if no overrides
      if (existing.length === 0) {
        existing = MOCK_ROWS.slice(0, 6)
          .map((row) => {
            const val = row[col.cellType as keyof typeof row];
            if (Array.isArray(val)) return val.join(", ");
            if (typeof val === "object" && val !== null && "name" in val)
              return (val as any).name;
            return String(val ?? "");
          })
          .filter(Boolean);
      }
      return {
        id: col.id,
        label: col.label,
        cellType: col.cellType,
        examples: existing,
      };
    });

    setGeneratingRows(true);
    try {
      // Gather ALL existing values per column (including previously generated) to avoid repetition
      const existingPerCol = colContext.map((c) => {
        const all = Object.entries(currentConfig.cellValues)
          .filter(([k]) => k.startsWith(`${c.id}--`))
          .map(([, v]) => v)
          .filter(Boolean);
        // Combine with mock row defaults
        const mockVals = MOCK_ROWS.slice(
          0,
          Math.min(currentConfig.rowCount, MOCK_ROWS.length)
        )
          .map((row) => {
            const val = row[c.cellType as keyof typeof row];
            if (Array.isArray(val)) return val.join(", ");
            if (typeof val === "object" && val !== null && "name" in val)
              return (val as any).name;
            return String(val ?? "");
          })
          .filter(Boolean);
        const combined = Array.from(new Set([...all, ...mockVals]));
        return { label: c.label, existing: combined };
      });

      const prompt = `Generate data for ${newRowIds.length} new table rows. The table has these columns:
${colContext.map((c) => `- "${c.label}" (type: ${c.cellType}) — examples: ${c.examples.join(", ")}`).join("\n")}

ALREADY USED values (DO NOT repeat these, generate NEW unique values):
${existingPerCol.map((c) => `- "${c.label}": ${c.existing.join(", ")}`).join("\n")}

Return ONLY a valid JSON object where each key is the column LABEL and the value is an array of exactly ${newRowIds.length} strings (one per row).
Example format: { "${colContext[0]?.label}": ["value1"], "${colContext[1]?.label}": ["value1"] }

Rules:
- Each array must have exactly ${newRowIds.length} string(s)
- For "tagGroup" columns: each string is comma-separated tags e.g. "Design, Marketing"
- Generate VARIED, UNIQUE values — do NOT repeat or closely resemble existing values
- Keep values concise and realistic
- Return ONLY the JSON object, no other text`;

      const res = await fetch("/api/anthropic/v1/messages", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "claude-haiku-4-5-20251001",
          max_tokens: 1000,
          messages: [{ role: "user", content: prompt }],
        }),
      });
      if (!res.ok) return;
      const data = await res.json();
      const raw = data.content[0].text
        .trim()
        .replace(/^```[a-z]*\n?/i, "")
        .replace(/```$/, "");
      const generated: Record<string, string[]> = JSON.parse(raw);

      // Pool of varied Unsplash photo IDs for generated imageAndText rows
      const unsplashIds = [
        "photo-1553062407-98eeb64c6a62",
        "photo-1578996953841-b187dbe4bc8a",
        "photo-1544947950-fa07a98d237f",
        "photo-1581235720704-06d3acfcb36f",
        "photo-1586495777744-4413f21062fa",
        "photo-1559056199-641a0ac8b55e",
        "photo-1595341888016-a392ef81b7de",
        "photo-1590874103328-eac38a683ce7",
        "photo-1608571423902-eed4a5ad8108",
        "photo-1576566588028-4147f3842f27",
      ];

      const newCellValues = { ...currentConfig.cellValues };
      const newImageUrlValues = { ...currentConfig.imageUrlValues };
      for (const col of currentConfig.columns) {
        const vals = generated[col.id] ?? generated[col.label];
        if (!vals) continue;
        newRowIds.forEach((rowId, i) => {
          if (vals[i]) {
            newCellValues[`${col.id}--${rowId}`] = vals[i];
          }
          // Assign a varied image for imageAndText columns
          if (col.cellType === "imageAndText") {
            const imgId = unsplashIds[(startIdx + i) % unsplashIds.length];
            newImageUrlValues[`${col.id}--${rowId}`] =
              `https://images.unsplash.com/${imgId}?w=80&h=80&fit=crop`;
          }
        });
      }
      onConfigChange({
        ...currentConfig,
        cellValues: newCellValues,
        imageUrlValues: newImageUrlValues,
      });
    } catch (err) {
      console.error("AI row generation failed:", err);
    } finally {
      setGeneratingRows(false);
    }
  }

  function handleRowCountChange(newCount: number) {
    const prev = config.rowCount;
    // Always update rowCount immediately
    const updated = { ...config, rowCount: newCount };
    onConfigChange(updated);
    if (newCount > prev && newCount > MOCK_ROWS.length) {
      generateRowsWithAI(newCount, prev, updated);
    }
  }

  // ── Export to JSON ────────────────────────────────────────────────────────

  function exportJSON() {
    const exported = {
      columns: config.columns.map((col) => ({
        id: col.id,
        label: col.label,
        cellType: col.cellType,
        width: col.width,
        key: col.key,
        bold: col.bold,
      })),
      rowActionsEnabled: config.rowActionsEnabled,
      rowActionItems: config.rowActionItems,
      rowsSelectable: config.rowsSelectable,
    };
    const blob = new Blob([JSON.stringify(exported, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = Object.assign(document.createElement("a"), {
      href: url,
      download: `${(configName.trim() || "table-config").toLowerCase().replace(/\s+/g, "-")}.json`,
    });
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  // ── Upload JSON ───────────────────────────────────────────────────────────

  function handleUploadJSON(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const data = JSON.parse(ev.target?.result as string);
        if (data.columns) {
          onConfigChange({
            ...config,
            columns: data.columns,
            cellValues: data.cellValues ?? config.cellValues,
            imageUrlValues: data.imageUrlValues ?? config.imageUrlValues,
            subtextValues: data.subtextValues ?? config.subtextValues,
            rowCount: data.rowCount ?? config.rowCount,
            rowsSelectable: data.rowsSelectable ?? config.rowsSelectable,
            rowActionsEnabled:
              data.rowActionsEnabled ?? config.rowActionsEnabled,
            rowActionItems: data.rowActionItems ?? config.rowActionItems,
            columnPrompts: data.columnPrompts ?? config.columnPrompts,
          });
        }
        if (data.tableName) onConfigNameChange(data.tableName);
        else if (file.name)
          onConfigNameChange(file.name.replace(/\.json$/, ""));
      } catch {}
      setShowLoadModal(false);
    };
    reader.readAsText(file);
    e.target.value = "";
  }

  // ── Save handler ──────────────────────────────────────────────────────────

  function handleSave() {
    onSave();
    if (saveToastTimer.current) clearTimeout(saveToastTimer.current);
    setSaveToast(true);
    saveToastTimer.current = setTimeout(() => setSaveToast(false), 2000);
  }

  // ── Render ────────────────────────────────────────────────────────────────

  // ── Drag logic ──────────────────────────────────────────────────────────────
  const [position, setPosition] = useState(() => ({
    x: typeof window !== "undefined" ? window.innerWidth - 320 - 16 : 16,
    y: 16,
  }));
  const isDragging = useRef(false);
  const dragOffset = useRef({ x: 0, y: 0 });
  const panelRef = useRef<HTMLDivElement>(null);

  const handlePanelDragStart = useCallback(
    (e: React.MouseEvent) => {
      isDragging.current = true;
      dragOffset.current = {
        x: e.clientX - position.x,
        y: e.clientY - position.y,
      };
      e.preventDefault();
    },
    [position]
  );

  useEffect(() => {
    function handleMouseMove(e: MouseEvent) {
      if (!isDragging.current) return;
      const panelW = panelRef.current?.offsetWidth ?? 320;
      const rawX = e.clientX - dragOffset.current.x;
      const rawY = e.clientY - dragOffset.current.y;
      setPosition({
        x: Math.max(-panelW + 80, Math.min(rawX, window.innerWidth - 80)),
        y: Math.max(0, Math.min(rawY, window.innerHeight - 48)),
      });
    }
    function handleMouseUp() {
      if (!isDragging.current) return;
      isDragging.current = false;
      // Snap to edges on release
      const panelW = panelRef.current?.offsetWidth ?? 320;
      const panelH = panelRef.current?.offsetHeight ?? 400;
      const pad = 16;
      const snap = 40;
      setPosition((prev) => {
        let { x, y } = prev;
        if (x < snap) x = pad;
        if (x + panelW > window.innerWidth - snap)
          x = window.innerWidth - panelW - pad;
        if (y < snap) y = pad;
        if (y + panelH > window.innerHeight - snap)
          y = window.innerHeight - panelH - pad;
        return { x, y };
      });
    }
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, []);

  return (
    <>
      {/* Floating panel */}
      <div
        ref={panelRef}
        className="fixed z-50 flex w-[320px] flex-col border border-(--slate-color-grey-100)/40"
        style={{
          backgroundColor: `rgba(248,244,255,${bgOpacity / 100})`,
          backdropFilter: `blur(${bgBlur}px)`,
          WebkitBackdropFilter: `blur(${bgBlur}px)`,
          borderRadius: 20,
          boxShadow:
            "0 -2px 16px rgba(0,0,0,0.08), 0 8px 32px rgba(0,0,0,0.12)",
          left: position.x,
          top: position.y,
          maxHeight: "calc(100vh - 32px)",
        }}
      >
        {/* ── Drag handle + toolbar ──────────────────────────────────────────── */}
        <div
          className="flex-shrink-0 cursor-grab border-b border-(--slate-color-grey-100)/30 px-4 pt-2 pb-3 select-none active:cursor-grabbing"
          onMouseDown={handlePanelDragStart}
        >
          {/* Drag indicator */}
          <div className="mb-2 flex justify-center">
            <div className="h-1 w-8 rounded-full bg-[#d1d5db]" />
          </div>
          <div
            className="flex items-center gap-1.5"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="relative min-w-0 flex-1">
              <Input
                value={configName}
                onChange={(e) => onConfigNameChange(e.target.value)}
                aria-label="Table name"
                placeholder="Table name"
                className="h-8 pr-8 text-xs"
              />
              <button
                onClick={onScrollToTable}
                className="absolute top-0 right-0 flex h-8 w-8 items-center justify-center text-[#9ca3af] transition-colors hover:text-[#374151]"
                title="Scroll to table"
              >
                <Crosshair size={14} />
              </button>
            </div>
            <div className="flex flex-shrink-0 gap-1.5">
              <UiButton
                variant="outline"
                size="sm"
                onClick={handleSave}
                className="h-8 text-[#4b5563]"
              >
                Save
              </UiButton>
              <UiButton
                variant="outline"
                size="icon"
                onClick={(e) => {
                  e.stopPropagation();
                  exportJSON();
                }}
                className="h-8 w-8 text-[#4b5563]"
                title="Export to JSON"
              >
                <UploadSimple size={14} />
              </UiButton>
            </div>
          </div>
        </div>

        {/* ── Panel content ────────────────────────────────────────────────── */}
        <div className="flex-1 overflow-y-auto px-4 py-4">
          {/* Header */}
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-[#374151]">
              Table Configuration
            </h2>
            <div className="flex items-center gap-1">
              <UiButton
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-[#9ca3af] hover:text-[#374151]"
                onClick={() => setShowLoadModal(true)}
                title="Load table"
              >
                <FolderOpen size={16} />
              </UiButton>
              <UiButton
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-[#9ca3af] hover:text-[#374151]"
                onClick={handleNewTable}
                title="New table"
              >
                <Plus size={16} />
              </UiButton>
              <UiButton
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-[#9ca3af] hover:text-[#374151]"
                onClick={onClose}
                title="Close panel"
              >
                <X size={16} />
              </UiButton>
            </div>
          </div>

          {/* ── Columns section ─────────────────────────────────────────────── */}
          <div className="mb-1">
            <p className="mb-2 text-[10px] font-medium tracking-wide text-[#9ca3af] uppercase">
              Columns
            </p>

            <div className="flex flex-col gap-2">
              {config.columns.map((col, index) => (
                <div
                  key={col.id}
                  draggable
                  onDragStart={() => handleDragStart(index)}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDrop={() => handleDrop(index)}
                  onDragEnd={handleDragEnd}
                  className={cn(
                    "bg-fs-surface-primary rounded border p-2.5 shadow-xs",
                    dragOverIndex === index
                      ? "border-t-2 border-[#275ec5]"
                      : "border-[#e5e7eb]"
                  )}
                >
                  {/* Row 1: handle + label + trash */}
                  <div className="mb-2 flex items-center gap-1.5">
                    <span className="flex-shrink-0 cursor-grab text-[#d1d5db]">
                      <DotsSixVertical size={16} />
                    </span>
                    <div
                      className="flex-1"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Input
                        value={col.label}
                        onChange={(e) =>
                          updateColumn(col.id, { label: e.target.value })
                        }
                        aria-label="Column label"
                        placeholder="Column label"
                        className="h-7 px-2 py-1 text-xs"
                      />
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteColumn(col.id);
                      }}
                      aria-label="Delete column"
                      className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded text-[#d1d5db] transition-colors hover:bg-[#fef2f2] hover:text-[#f87171]"
                    >
                      <Trash size={14} />
                    </button>
                  </div>

                  {/* Row 2: cell type + width + bold */}
                  <div className="flex items-center gap-1.5">
                    <div
                      className="min-w-0 flex-1"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <UiSelect
                        value={col.cellType}
                        onValueChange={(val: string) =>
                          updateCellType(col.id, val as CellType)
                        }
                      >
                        <SelectTrigger
                          aria-label="Cell type"
                          className="h-7 text-xs"
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {CELL_TYPE_OPTIONS.map((o) => (
                            <SelectItem key={o.value} value={o.value}>
                              {o.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </UiSelect>
                    </div>

                    <div
                      className="w-[72px] flex-shrink-0"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <UiSelect
                        value={col.width.toString()}
                        onValueChange={(val: string) =>
                          updateColumn(col.id, { width: Number(val) })
                        }
                      >
                        <SelectTrigger
                          aria-label="Column width"
                          className="h-7 text-xs"
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {WIDTH_OPTIONS.map((o) => (
                            <SelectItem key={o.value} value={o.value}>
                              {o.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </UiSelect>
                    </div>

                    {(col.cellType === "text" ||
                      col.cellType === "imageAndText") && (
                      <div
                        className="flex flex-shrink-0 items-center gap-1"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <UiCheckbox
                          id={`bold-${col.id}`}
                          checked={col.bold}
                          onCheckedChange={(checked) =>
                            updateColumn(col.id, { bold: checked === true })
                          }
                        />
                        <Label
                          htmlFor={`bold-${col.id}`}
                          className="cursor-pointer text-xs font-normal text-[#6b7280]"
                        >
                          Bold
                        </Label>
                      </div>
                    )}
                  </div>

                  {/* AI fill row */}
                  <div
                    className="mt-2 flex items-center gap-1.5 border-t border-dashed border-[#e5e7eb] pt-2"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Sparkle
                      size={12}
                      style={{ color: "#275ec5", flexShrink: 0 }}
                    />
                    <Input
                      type="text"
                      placeholder="Describe content..."
                      value={config.columnPrompts[col.id] ?? ""}
                      onChange={(e) =>
                        onConfigChange({
                          ...config,
                          columnPrompts: {
                            ...config.columnPrompts,
                            [col.id]: e.target.value,
                          },
                        })
                      }
                      onKeyDown={(e) => {
                        if (e.key === "Enter") fillColumnWithAI(col);
                      }}
                      onClick={(e) => e.stopPropagation()}
                      className="h-7 min-w-0 flex-1 px-2 py-1 text-xs"
                    />
                    <button
                      disabled={generatingColumn === col.id}
                      onClick={(e) => {
                        e.stopPropagation();
                        fillColumnWithAI(col);
                      }}
                      aria-label="Fill column with AI"
                      className="hover:bg-fs-surface-primary flex h-7 w-7 flex-shrink-0 items-center justify-center rounded border border-[#e5e7eb] text-[#6b7280] transition-colors hover:border-[#d1d5db] disabled:opacity-40"
                    >
                      {generatingColumn === col.id ? (
                        <span
                          className="block h-3 w-3 rounded-full border-2 border-current border-t-transparent"
                          style={{ animation: "spin 0.6s linear infinite" }}
                        />
                      ) : (
                        <svg
                          width="10"
                          height="10"
                          viewBox="0 0 10 10"
                          fill="none"
                        >
                          <path
                            d="M2 5h6M5.5 2l3 3-3 3"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Add column button */}
            <div className="mt-2" onClick={(e) => e.stopPropagation()}>
              <UiButton
                variant="outline"
                size="sm"
                onClick={addColumn}
                className="w-full gap-1.5 text-[#6b7280]"
              >
                <Plus size={14} />
                Add Column
              </UiButton>
            </div>
          </div>

          {/* ── Rows ─────────────────────────────────────────────────────────── */}
          <div className="mt-5 border-t border-[#e5e7eb] pt-4">
            <p className="mb-2.5 text-[10px] font-medium tracking-wide text-[#9ca3af] uppercase">
              Rows
            </p>
            <div
              className="flex w-fit items-stretch [&>*]:focus-visible:relative [&>*]:focus-visible:z-10 [&>*:not(:first-child)]:rounded-l-none [&>*:not(:first-child)]:border-l-0 [&>*:not(:last-child)]:rounded-r-none"
              onClick={(e) => e.stopPropagation()}
              role="group"
            >
              <UiButton
                variant="outline"
                size="icon"
                className="h-7 w-7"
                onClick={() => {
                  if (config.rowCount > 1)
                    handleRowCountChange(config.rowCount - 1);
                }}
                disabled={config.rowCount <= 1 || generatingRows}
              >
                <span className="text-sm leading-none">−</span>
              </UiButton>
              <RowCountInput
                value={config.rowCount}
                onChange={handleRowCountChange}
              />
              <UiButton
                variant="outline"
                size="icon"
                className="h-7 w-7"
                onClick={() => {
                  handleRowCountChange(config.rowCount + 1);
                }}
                disabled={generatingRows}
              >
                <span className="text-sm leading-none">+</span>
              </UiButton>
            </div>
            {generatingRows && (
              <p className="mt-1.5 flex items-center gap-1.5 text-[10px] text-[#9ca3af]">
                <span
                  className="block h-3 w-3 rounded-full border-2 border-[#9ca3af] border-t-transparent"
                  style={{ animation: "spin 0.6s linear infinite" }}
                />
                Generating row data...
              </p>
            )}
          </div>

          {/* ── Row Selection ────────────────────────────────────────────────── */}
          <div className="mt-5 border-t border-[#e5e7eb] pt-4">
            <p className="mb-2.5 text-[10px] font-medium tracking-wide text-[#9ca3af] uppercase">
              Row Selection
            </p>
            <div
              className="flex items-center gap-2"
              onClick={(e) => e.stopPropagation()}
            >
              <UiCheckbox
                id="row-selection-toggle"
                checked={config.rowsSelectable}
                onCheckedChange={(checked) =>
                  onConfigChange({
                    ...config,
                    rowsSelectable: checked === true,
                  })
                }
              />
              <Label
                htmlFor="row-selection-toggle"
                className="cursor-pointer text-sm font-normal text-[#4b5563]"
              >
                Enable row selection
              </Label>
            </div>
          </div>

          {/* ── Row Actions ──────────────────────────────────────────────────── */}
          <div className="mt-5 border-t border-[#e5e7eb] pt-4">
            <p className="mb-2.5 text-[10px] font-medium tracking-wide text-[#9ca3af] uppercase">
              Row Actions
            </p>
            <div
              className="mb-2 flex items-center gap-2"
              onClick={(e) => e.stopPropagation()}
            >
              <UiCheckbox
                id="row-actions-toggle"
                checked={config.rowActionsEnabled}
                onCheckedChange={(checked) =>
                  onConfigChange({
                    ...config,
                    rowActionsEnabled: checked === true,
                  })
                }
              />
              <Label
                htmlFor="row-actions-toggle"
                className="cursor-pointer text-sm font-normal text-[#4b5563]"
              >
                Enable row actions
              </Label>
            </div>
            {config.rowActionsEnabled && (
              <>
                <div
                  className="flex flex-col gap-1.5"
                  onClick={(e) => e.stopPropagation()}
                >
                  {config.rowActionItems.map((action) => (
                    <div key={action.id} className="flex flex-col gap-1">
                      <div className="flex items-center gap-1.5">
                        <div className="min-w-0 flex-1">
                          <Input
                            value={action.label}
                            onChange={(e) =>
                              updateRowAction(action.id, {
                                label: e.target.value,
                              })
                            }
                            aria-label="Action label"
                            placeholder="Action label"
                            className="h-7 px-2 py-1 text-xs"
                          />
                        </div>
                        <div className="w-[105px] flex-shrink-0">
                          <UiSelect
                            value={action.display}
                            onValueChange={(val: string) =>
                              updateRowAction(action.id, {
                                display: val as "inline" | "overflow",
                              })
                            }
                          >
                            <SelectTrigger
                              aria-label="Display type"
                              className="h-7 text-xs"
                            >
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="inline">Inline</SelectItem>
                              <SelectItem value="overflow">Overflow</SelectItem>
                            </SelectContent>
                          </UiSelect>
                        </div>
                        <button
                          onClick={() => deleteRowAction(action.id)}
                          aria-label="Delete action"
                          className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded text-[#d1d5db] transition-colors hover:bg-[#fef2f2] hover:text-[#f87171]"
                        >
                          <Trash size={14} />
                        </button>
                      </div>
                      {action.display === "inline" && (
                        <div className="w-[110px] flex-shrink-0">
                          <UiSelect
                            value={action.variant}
                            onValueChange={(val: string) =>
                              updateRowAction(action.id, {
                                variant: val as RowAction["variant"],
                              })
                            }
                          >
                            <SelectTrigger
                              aria-label="Button variant"
                              className="h-7 text-xs"
                            >
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="primary">Primary</SelectItem>
                              <SelectItem value="secondary">
                                Secondary
                              </SelectItem>
                              <SelectItem value="tertiary">Tertiary</SelectItem>
                              <SelectItem value="plain">Plain</SelectItem>
                            </SelectContent>
                          </UiSelect>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                <div className="mt-2" onClick={(e) => e.stopPropagation()}>
                  <UiButton
                    variant="outline"
                    size="sm"
                    onClick={addRowAction}
                    className="w-full gap-1.5 text-[#6b7280]"
                  >
                    <Plus size={14} />
                    Add Action
                  </UiButton>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Save toast */}
      <div
        className={cn(
          "fixed right-6 bottom-6 z-50 flex items-center gap-2 rounded-md bg-[#111827] px-4 py-2.5 text-xs font-medium text-(--slate-color-action-text-inverse) shadow-lg transition-all duration-200",
          saveToast
            ? "translate-y-0 opacity-100"
            : "pointer-events-none translate-y-2 opacity-0"
        )}
      >
        <Check size={14} weight="bold" />
        Saved
      </div>

      {/* Load modal */}
      <Dialog open={showLoadModal} onOpenChange={setShowLoadModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Load table</DialogTitle>
          </DialogHeader>

          {/* Saved list */}
          <div className="flex max-h-64 flex-col gap-1 overflow-y-auto">
            {savedConfigs.length === 0 ? (
              <p className="py-2 text-xs text-[#9ca3af]">
                No saved tables yet.
              </p>
            ) : (
              savedConfigs.map((cfg) => (
                <div
                  key={cfg.id}
                  className="hover:bg-fs-surface-primary flex items-center gap-2 rounded px-1 py-1.5"
                >
                  <span className="flex-1 truncate text-sm text-[#374151]">
                    {cfg.name}
                  </span>
                  <span className="flex-shrink-0 text-[10px] text-[#9ca3af]">
                    {new Date(cfg.savedAt).toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                    })}
                  </span>
                  <button
                    onClick={() => {
                      onLoad(cfg);
                      setShowLoadModal(false);
                    }}
                    className="flex-shrink-0 rounded border border-[#e5e7eb] px-2 py-0.5 text-xs text-[#6b7280] transition-colors hover:border-[#9ca3af] hover:text-[#374151]"
                  >
                    Load
                  </button>
                  <button
                    onClick={() => onDelete(cfg.id)}
                    className="flex-shrink-0 text-[#d1d5db] transition-colors hover:text-[#f87171]"
                    aria-label="Delete"
                  >
                    <X size={14} />
                  </button>
                </div>
              ))
            )}
          </div>

          <div className="border-t border-(--slate-color-action-surface-inverse-hover) pt-3">
            <p className="mb-2 text-xs text-[#9ca3af]">Upload from file</p>
            <input
              ref={uploadInputRef}
              type="file"
              accept=".json"
              onChange={handleUploadJSON}
              className="hidden"
            />
            <button
              onClick={() => uploadInputRef.current?.click()}
              className="flex w-full items-center gap-2 rounded border border-[#e5e7eb] px-3 py-1.5 text-xs text-[#6b7280] transition-colors hover:border-[#9ca3af] hover:text-[#374151]"
            >
              <UploadSimple size={14} />
              Upload JSON config
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
